const PRODUCT_SELECTOR = '[data-component="n4-listicle-product"][data-blok-c]'
const TARGET_ATTRIBUTE = 'data-blok-uid'

export const isStoryblokVisualEditor = (search: string, embedded: boolean): boolean =>
  embedded && search.includes('_storyblok') && new URLSearchParams(search).get('_story_compare') !== 'true'

interface Selection {
  element: HTMLElement
  index: number
}

const editorId = (element: HTMLElement): string | null => {
  try {
    const annotation: unknown = JSON.parse(element.getAttribute('data-blok-c') ?? '')
    if (
      annotation &&
      typeof annotation === 'object' &&
      'uid' in annotation &&
      typeof annotation.uid === 'string' &&
      'id' in annotation &&
      (typeof annotation.id === 'string' || typeof annotation.id === 'number')
    ) {
      return `${annotation.id}-${annotation.uid}`
    }
  } catch {
    // A malformed annotation must not break the rest of the editor.
  }
  return null
}

const productGroups = (document: Document): Map<string, HTMLElement[]> => {
  const groups = new Map<string, HTMLElement[]>()
  for (const element of document.querySelectorAll<HTMLElement>(PRODUCT_SELECTOR)) {
    const id = editorId(element)
    if (id) groups.set(id, [...(groups.get(id) ?? []), element])
  }
  return groups
}

const targetFor = (elements: HTMLElement[], selected?: Selection): HTMLElement => {
  if (selected) {
    if (elements.includes(selected.element)) return selected.element
    // Live RSC rendering can replace the DOM node while keeping its occurrence.
    if (elements[selected.index]) return elements[selected.index]
  }
  return elements.find((element) => element.classList.contains('listicle-product')) ?? elements[0]
}

const setTarget = (id: string, elements: HTMLElement[], target: HTMLElement) => {
  for (const element of elements) {
    if (element === target) {
      if (element.getAttribute(TARGET_ATTRIBUTE) !== id) element.setAttribute(TARGET_ATTRIBUTE, id)
    } else if (element.hasAttribute(TARGET_ATTRIBUTE)) {
      element.removeAttribute(TARGET_ATTRIBUTE)
    }
  }
}

// Storyblok opens a blok using data-blok-c, but locates its outline/scroll target
// with the first matching data-blok-uid. Repeated product summaries therefore
// need one active target, while every copy retains its real editing annotation.
// Install only inside the Visual Editor; never intercept public-page clicks.
export const installEditorProductTargets = (document: Document): (() => void) => {
  const selected = new Map<string, Selection>()
  const synchronize = () => {
    const groups = productGroups(document)
    for (const id of selected.keys()) if (!groups.has(id)) selected.delete(id)
    for (const [id, elements] of groups) {
      const target = targetFor(elements, selected.get(id))
      setTarget(id, elements, target)
      if (selected.has(id)) selected.set(id, { element: target, index: elements.indexOf(target) })
    }
  }
  const onClick = (event: MouseEvent) => {
    const element = event.target instanceof Element ? event.target.closest<HTMLElement>(PRODUCT_SELECTOR) : null
    const id = element && editorId(element)
    if (!element || !id) return
    const elements = productGroups(document).get(id)
    if (!elements || elements.length < 2) return
    selected.set(id, { element, index: elements.indexOf(element) })
    setTarget(id, elements, element)
    // Keep native links and React section links from navigating before the
    // bridge receives the click. Propagation continues to Storyblok's listener.
    event.preventDefault()
  }
  synchronize()
  document.addEventListener('click', onClick, true)
  const observer = new MutationObserver(synchronize)
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['data-blok-c', TARGET_ATTRIBUTE],
  })
  return () => {
    observer.disconnect()
    document.removeEventListener('click', onClick, true)
    for (const [id, elements] of productGroups(document)) {
      for (const element of elements) element.setAttribute(TARGET_ATTRIBUTE, id)
    }
  }
}
